/**
 * Sequence Create/Edit Page
 * Form to create or edit a message sequence with condition-based triggers,
 * then redirect to detail page for step building.
 */

import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  createSequence,
  getSequence,
  updateSequence,
} from "../services/sequences.service";
import { getZuoraEntitlementValues } from "../services/zuoraEntitlements.service";
import EventPicker from "../components/events/EventPicker";
import {
  listActiveGroups,
  type SubscriptionGroup,
} from "../services/subscriptionGroups.service";
import { supabase } from "../services/supabase";
import { fetchAllRows, pageByNameThenId } from "../utils/fetchAllRows";
import Button from "../components/common/Button";
import Input from "../components/common/Input";
import Card from "../components/ui/Card";
import QueryBuilder from "../components/lists/QueryBuilder";
import {
  ArrowLeftIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import type {
  EvaluationCadence,
  SequenceTriggerType,
} from "../types/sequence.types";
import type { QueryCriteria } from "../types/list.types";
import {
  EVALUATION_CADENCE_OPTIONS,
  TRIGGER_DATE_FIELD_OPTIONS,
  TRIGGER_TYPE_OPTIONS,
  TRIGGER_TYPE_ENROLLS_ON,
  triggerTypeUsesListForEnrollment,
} from "../types/sequence.types";

const SequenceCreate: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  // Basic info
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  // Trigger configuration
  // 2026-05-30: triggerType is an explicit operator choice independent of
  // cadence. Hidden in the UI when an event is linked (eventTriggerMode
  // handles event_registration / event_attended) but the form state is
  // still tracked so the save handler can write the right value.
  const [triggerType, setTriggerType] = useState<SequenceTriggerType>("manual");
  const [evaluationCadence, setEvaluationCadence] =
    useState<EvaluationCadence>("manual");
  const [triggerConditions, setTriggerConditions] = useState<QueryCriteria>({
    operator: "AND",
    conditions: [],
  });
  // Explicit opt-in for "send to all new members" when triggerType is
  // member_signup AND the operator deliberately wants no tier scoping.
  // Persisted to metadata.send_to_all_new_members so a future DB CHECK
  // constraint can read it (see todo_sequences_db_check_member_signup_requires_conditions).
  const [sendToAllNewMembers, setSendToAllNewMembers] = useState(false);

  const [product, setProduct] = useState("");
  const [productAudience, setProductAudience] = useState<
    "primary" | "secondary" | "both"
  >("primary");
  const productOptions = useQuery({
    queryKey: ["product-journey-options"],
    queryFn: () => getZuoraEntitlementValues("plan"),
    enabled: triggerType === "product_acquired",
  });

  // Date-based trigger
  const [triggerDateField, setTriggerDateField] = useState("");
  const [offsetDays, setOffsetDays] = useState(0);
  const [offsetDirection, setOffsetDirection] = useState<"before" | "after">(
    "before",
  );

  // Event link
  const [eventId, setEventId] = useState("");
  // Pardot parity Tier 1 (2026-05-17). When event is linked, choose
  // whether enrollment fires on registration or on check-in (attended).
  const [eventTriggerMode, setEventTriggerMode] = useState<
    "event_registration" | "event_attended"
  >("event_registration");

  // List link
  const [listId, setListId] = useState("");

  // ASM email subscription category (marketing group). "" = none.
  const [subscriptionGroupId, setSubscriptionGroupId] = useState("");

  // Pardot parity Tier 1 — allow re-enrollment after a completed/cancelled run.
  const [allowReenrollment, setAllowReenrollment] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loaded, setLoaded] = useState(!isEditMode); // Skip loading state for create
  // Optimistic-concurrency token captured ONCE in the hydration block below.
  // Do NOT read existingSequence.updated_at at save time: a background
  // refetch can refresh that value while `loaded` blocks rehydrating the form
  // fields, so the save would pair STALE form fields with the NEW timestamp
  // and slip past the updated_at guard (re-audit Medium, 2026-07-19).
  const [hydratedUpdatedAt, setHydratedUpdatedAt] = useState<
    string | undefined
  >(undefined);

  // Load existing sequence for edit mode. staleTime/gcTime 0: hydrating this
  // form from a session-cached copy and then saving writes the FULL trigger
  // config back, silently reverting any change made since the cache was
  // populated (another tab/operator or direct SQL) — the Classroom
  // trigger_type lost-update, 2026-07-16. Always hydrate from a fresh fetch.
  const { data: existingSequence } = useQuery({
    queryKey: ["sequence-edit", id],
    queryFn: () => getSequence(id!),
    enabled: isEditMode,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
  });

  // Populate form when existing sequence loads — ONCE (gated by !loaded).
  // Without the gate, any background refetch (e.g. window refocus) re-runs
  // this and clobbers the operator's unsaved edits.
  useEffect(() => {
    if (existingSequence && isEditMode && !loaded) {
      setProduct(existingSequence.trigger_config?.product || "");
      setProductAudience(
        existingSequence.trigger_config?.audience || "primary",
      );
      setName(existingSequence.name || "");
      setDescription(existingSequence.description || "");
      setEvaluationCadence(existingSequence.evaluation_cadence || "manual");
      if (existingSequence.trigger_conditions) {
        setTriggerConditions(existingSequence.trigger_conditions);
      }
      setTriggerDateField(existingSequence.trigger_date_field || "");

      // Parse signed offset back to absolute + direction
      const offset = existingSequence.trigger_offset_days || 0;
      if (offset >= 0) {
        setOffsetDays(offset);
        setOffsetDirection("before");
      } else {
        setOffsetDays(Math.abs(offset));
        setOffsetDirection("after");
      }

      setEventId(existingSequence.event_id || "");
      setEventTriggerMode(
        existingSequence.trigger_type === "event_attended"
          ? "event_attended"
          : "event_registration",
      );
      // R3-MR1 (audit 2026-05-30): hydrate triggerType from the saved value
      // when no event is linked. When an event IS linked, the saved
      // trigger_type is event_registration/event_attended — eventTriggerMode
      // covers that. Leave triggerType at default so the dropdown is in a
      // sensible state if the operator later unlinks the event.
      if (
        existingSequence.trigger_type &&
        existingSequence.trigger_type !== "event_registration" &&
        existingSequence.trigger_type !== "event_attended"
      ) {
        setTriggerType(existingSequence.trigger_type);
      }
      // Hydrate the explicit "all new members" opt-in from metadata.
      setSendToAllNewMembers(
        (existingSequence.metadata as Record<string, unknown> | null)?.[
          existingSequence.trigger_type === "reinstated_member"
            ? "send_to_all_reinstated_members"
            : "send_to_all_new_members"
        ] === true,
      );
      setListId(existingSequence.list_id || "");
      setSubscriptionGroupId(existingSequence.subscription_group_id || "");
      setAllowReenrollment(existingSequence.allow_reenrollment === true);
      // Capture the concurrency token from the SAME snapshot the form
      // hydrated from (see state declaration for why).
      setHydratedUpdatedAt(existingSequence.updated_at);
      setLoaded(true);
    }
  }, [existingSequence, isEditMode]);

  // Fetch lists for dropdown
  const { data: lists } = useQuery({
    queryKey: ["lists-for-sequence"],
    queryFn: async () => {
      // Paged: PROD has more segments than PostgREST's 1000-row cap.
      return fetchAllRows<{
        id: string;
        name: string;
        contact_count: number | null;
      }>((from, to) =>
        pageByNameThenId(
          supabase
            .from("lists")
            .select("id, name, contact_count")
            .is("archived_at", null),
          from,
          to,
        ),
      );
    },
  });

  // Fetch active subscription categories for the ASM dropdown
  const { data: subscriptionGroups } = useQuery({
    queryKey: ["active-subscription-groups"],
    queryFn: () => listActiveGroups().catch(() => [] as SubscriptionGroup[]),
  });

  const showTriggerConfig = evaluationCadence !== "manual";

  const createMutation = useMutation({
    mutationFn: createSequence,
    onSuccess: (data) => {
      navigate(`/sequences/${data.id}`);
    },
  });

  const updateMutation = useMutation({
    // Pass the updated_at captured at hydration — the save is refused
    // (instead of silently overwriting) if someone else saved this sequence
    // since the operator opened it.
    mutationFn: (input: Parameters<typeof updateSequence>[1]) => {
      if (!hydratedUpdatedAt) {
        throw new Error(
          "This page hasn't finished loading the sequence. Reload and try again.",
        );
      }
      return updateSequence(id!, input, hydratedUpdatedAt);
    },
    onSuccess: () => {
      navigate(`/sequences/${id}`);
    },
  });

  const mutation = isEditMode ? updateMutation : createMutation;

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) {
      newErrors.name = "Sequence name is required";
    }

    // Reject EMPTY condition groups anywhere in the tree. An empty group compiles to a
    // match-all (1=1) in the sequence-trigger compiler AND counts toward
    // triggerConditions.conditions.length, so it silently satisfies the per-trigger
    // "conditions required" guard below while broadening enrollment to everyone. The
    // compiler now fails closed on these too, but catch it at save time with a clear message.
    const groupHasEmptyDescendant = (grp: {
      conditions?: unknown[];
    }): boolean =>
      (grp.conditions ?? []).some((child) => {
        const c = child as { conditions?: unknown[] };
        return Array.isArray(c.conditions)
          ? c.conditions.length === 0 || groupHasEmptyDescendant(c)
          : false;
      });
    if (
      triggerConditions.conditions.length > 0 &&
      groupHasEmptyDescendant(triggerConditions)
    ) {
      newErrors.triggerConditions =
        "Remove or complete the empty condition group — an empty group matches everyone " +
        "and would over-enroll contacts.";
    }

    // R2-MR2 / R3-MR2 (audit 2026-05-30): Enrollment Trigger guards.
    // Only enforce when no event is linked — event-linked sequences derive
    // trigger from eventTriggerMode and aren't subject to these guards.
    if (!eventId) {
      if (triggerType === "product_acquired" && !product.trim()) {
        newErrors.product = "Choose the acquired product.";
      }
      // The server excludes every recipient of a product journey with no email category
      // (subscription_group_missing), so a journey saved without one would enroll nobody.
      if (triggerType === "product_acquired" && !subscriptionGroupId) {
        newErrors.subscriptionGroupId =
          "Choose an email subscription category. A product journey without one enrolls nobody.";
      }
      const hasConditions = triggerConditions.conditions.length > 0;
      if (
        triggerType === "member_signup" &&
        !hasConditions &&
        !sendToAllNewMembers
      ) {
        newErrors.triggerConditions =
          "Filter Conditions are required for the New Member trigger. " +
          "Without them, every new member would be enrolled regardless of tier. " +
          'Use the conditions builder below, or check "Send to all new members" ' +
          "if that is the intent.";
      }
      if (
        triggerType === "reinstated_member" &&
        !hasConditions &&
        !sendToAllNewMembers
      ) {
        newErrors.triggerConditions =
          "Filter Conditions are required for the Reinstated Member trigger. " +
          "Without them, every returning member would be enrolled regardless of tier. " +
          'Use the conditions builder below, or check "Send to all reinstated members" ' +
          "if that is the intent.";
      }
      if (triggerType === "list_membership" && !listId) {
        newErrors.listId = "Choose a list for the List Membership trigger.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    // Compute signed offset: positive = before date, negative = after date
    const signedOffset =
      offsetDirection === "before" ? offsetDays : -offsetDays;

    // Determine if conditions were actually set
    const hasConditions = triggerConditions.conditions.length > 0;

    // Trigger type:
    //   - 'event_attended' or 'event_registration' when an event is linked
    //     (Pardot Tier 1 — 2026-05-17). eventTriggerMode is the operator's
    //     pick within the event branch.
    //   - Otherwise the operator's explicit Enrollment Trigger choice
    //     (manual / member_signup / list_membership). Audit 2026-05-30
    //     scrapped the cadence-based derivation that caused the
    //     over-enrollment incident — trigger and cadence are now
    //     independent in the UI just as they are in the schema.
    const finalTriggerType: SequenceTriggerType = eventId
      ? eventTriggerMode
      : triggerType;

    // Persist the "all new members" opt-in to metadata so the future
    // DB CHECK constraint can read it
    // (see todo_sequences_db_check_member_signup_requires_conditions).
    // Only write the flag when it's true AND the trigger calls for it;
    // for any other trigger or false state, clear it so stale flags
    // don't leak across reconfigurations.
    const metadataPatch: Record<string, unknown> = {
      ...((existingSequence?.metadata as Record<string, unknown> | null) ?? {}),
    };
    if (finalTriggerType === "member_signup" && sendToAllNewMembers) {
      metadataPatch.send_to_all_new_members = true;
    } else {
      delete metadataPatch.send_to_all_new_members;
    }
    if (finalTriggerType === "reinstated_member" && sendToAllNewMembers) {
      metadataPatch.send_to_all_reinstated_members = true;
    } else {
      delete metadataPatch.send_to_all_reinstated_members;
    }
    const metadata =
      Object.keys(metadataPatch).length > 0 ? metadataPatch : null;

    const input = {
      name: name.trim(),
      description: description.trim() || undefined,
      trigger_type: finalTriggerType,
      trigger_config:
        finalTriggerType === "product_acquired"
          ? { product: product.trim(), audience: productAudience }
          : existingSequence?.trigger_config || {},
      evaluation_cadence:
        finalTriggerType === "product_acquired" ||
        finalTriggerType === "reinstated_member"
          ? ("immediate" as const)
          : evaluationCadence,
      trigger_conditions:
        hasConditions && showTriggerConfig ? triggerConditions : null,
      trigger_date_field:
        finalTriggerType !== "product_acquired" &&
        finalTriggerType !== "reinstated_member" &&
        triggerDateField &&
        showTriggerConfig
          ? triggerDateField
          : null,
      trigger_offset_days:
        finalTriggerType !== "product_acquired" &&
        finalTriggerType !== "reinstated_member" &&
        triggerDateField &&
        showTriggerConfig
          ? signedOffset
          : 0,
      event_id: eventId || null,
      list_id: listId || undefined,
      subscription_group_id: subscriptionGroupId || null,
      allow_reenrollment:
        finalTriggerType === "product_acquired" ? false : allowReenrollment,
      metadata,
    };

    if (isEditMode) {
      updateMutation.mutate(input);
    } else {
      createMutation.mutate({
        ...input,
        status: "draft",
      });
    }
  };

  if (!loaded) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading sequence...</div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            navigate(isEditMode ? `/sequences/${id}` : "/sequences")
          }
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </Button>
        <h1 className="text-3xl font-bold text-gray-900">
          {isEditMode ? "Edit Sequence" : "Create Sequence"}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Section 1: Basic Info */}
        <Card>
          <div className="p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Basic Information
            </h2>
            <Input
              label="Sequence Name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) {
                  setErrors((prev) => ({ ...prev, name: "" }));
                }
              }}
              error={errors.name}
              placeholder="e.g. New Member Onboarding"
              required
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="What does this sequence do?"
              />
            </div>
          </div>
        </Card>

        {/* Section 2: Entry Trigger */}
        <Card>
          <div className="p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Entry Trigger
            </h2>
            <p className="text-sm text-gray-500">
              <strong>Enrollment Trigger</strong> controls HOW contacts get into
              this sequence (manual, on member signup, etc.).{" "}
              <strong>Cadence</strong> controls HOW FAST the first step fires
              once they're enrolled. These are independent.
            </p>

            {/* Enrollment Trigger dropdown — hidden when event-linked
                (eventTriggerMode handles that branch below in the Event
                Link section). Audit 2026-05-30 — separate from cadence. */}
            {!eventId && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Enrollment Trigger <span className="text-red-500">*</span>
                </label>
                <select
                  value={triggerType}
                  onChange={(e) => {
                    setTriggerType(e.target.value as SequenceTriggerType);
                    if (e.target.value === "product_acquired") {
                      setEvaluationCadence("immediate");
                      setAllowReenrollment(false);
                      setTriggerDateField("");
                    }
                    // Reinstated journeys fire on the activation itself; a scheduled cadence would
                    // enroll every contact matching the conditions (the server normalizes too).
                    if (e.target.value === "reinstated_member") {
                      setEvaluationCadence("immediate");
                      setTriggerDateField("");
                    }
                    // Switching trigger clears any stale validation
                    if (errors.triggerConditions) {
                      setErrors((prev) => ({
                        ...prev,
                        triggerConditions: "",
                      }));
                    }
                    if (errors.listId) {
                      setErrors((prev) => ({ ...prev, listId: "" }));
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {TRIGGER_TYPE_OPTIONS.filter(
                    (opt) =>
                      opt.value !== "event_registration" &&
                      opt.value !== "event_attended" &&
                      // 2026-05-30 post-deploy audit: list_membership has
                      // no backend runtime evaluator. auto_enroll_new_member
                      // only fires for trigger_type='member_signup'. If an
                      // operator picks list_membership here, the sequence
                      // saves correctly but nothing enrolls contacts when
                      // they join the list — silent zero-enrollment.
                      // Hide until the backend evaluator ships; see
                      // todo_sequences_list_membership_runtime_evaluator.md
                      opt.value !== "list_membership",
                  ).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label} — {opt.description}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {triggerType === "product_acquired" && !eventId && (
              <div className="space-y-3 border rounded p-4">
                <label className="block">
                  Acquired product
                  <select
                    aria-label="Acquired product"
                    className="block w-full border rounded p-2"
                    value={product}
                    onChange={(e) => setProduct(e.target.value)}
                  >
                    <option value="">Choose a product</option>
                    {product &&
                      !productOptions.data?.some(
                        (p) => p.value === product,
                      ) && <option value={product}>{product}</option>}
                    {productOptions.data?.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.value}
                      </option>
                    ))}
                  </select>
                </label>
                {productOptions.isError && (
                  <p role="alert">
                    Unable to load products. Retry before choosing a new
                    product.
                  </p>
                )}
                {errors.product && <p role="alert">{errors.product}</p>}
                <label className="block">
                  Audience
                  <select
                    aria-label="Product audience"
                    className="block border rounded p-2"
                    value={productAudience}
                    onChange={(e) =>
                      setProductAudience(
                        e.target.value as typeof productAudience,
                      )
                    }
                  >
                    <option value="primary">Primary members</option>
                    <option value="secondary">Secondary members</option>
                    <option value="both">Primary and secondary members</option>
                  </select>
                </label>
                <p>
                  Enroll once per member when this product becomes active on
                  their account, including upgrades. Renewals and repeated syncs
                  do not restart the journey. Existing holders require a
                  reviewed catch-up from the Enrollments tab.
                </p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cadence <span className="text-red-500">*</span>
              </label>
              <select
                disabled={
                  (triggerType === "product_acquired" ||
                    triggerType === "reinstated_member") &&
                  !eventId
                }
                value={evaluationCadence}
                onChange={(e) =>
                  setEvaluationCadence(e.target.value as EvaluationCadence)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {EVALUATION_CADENCE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label} — {opt.description}
                  </option>
                ))}
              </select>
            </div>

            {evaluationCadence === "immediate" && (
              <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <InformationCircleIcon className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
                <p className="text-sm text-blue-800">
                  Immediate cadence fires the first step as soon as a contact is
                  enrolled. How contacts get enrolled is controlled by{" "}
                  <strong>Enrollment Trigger</strong> above — cadence is
                  independent of trigger.
                </p>
              </div>
            )}

            {showTriggerConfig && (
              <div className="space-y-3 pt-2">
                <label className="block text-sm font-medium text-gray-700">
                  Filter Conditions
                  {(triggerType === "member_signup" ||
                    triggerType === "reinstated_member") &&
                    !eventId && <span className="text-red-500"> *</span>}
                </label>
                <p className="text-xs text-gray-500">
                  {triggerType === "member_signup" && !eventId
                    ? "Required — scope this sequence to a specific member tier so it doesn't enroll every new member. Use 'Send to all new members' below only if a global welcome is the explicit intent."
                    : triggerType === "reinstated_member" && !eventId
                      ? "Required — scope this sequence to a specific member tier so it doesn't enroll every returning member. Use 'Send to all reinstated members' below only if that is the explicit intent."
                      : triggerType === "product_acquired"
                        ? "Optional additional filters. The acquired product and selected audience always apply."
                        : "Only contacts matching these conditions will be enrolled. Leave empty to match all contacts."}
                </p>
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <QueryBuilder
                    group={triggerConditions}
                    onChange={(c) => {
                      setTriggerConditions(c);
                      if (errors.triggerConditions) {
                        setErrors((prev) => ({
                          ...prev,
                          triggerConditions: "",
                        }));
                      }
                    }}
                    // 2026-05-29: segment_membership is now allowed for
                    // sequence entry triggers. The backend compiler
                    // (build_query_criteria_sql, migration 2026-05-29-001)
                    // normalizes segment_membership → segment and
                    // equals/not_equals → is_on_list/is_not_on_list, then
                    // emits an EXISTS subquery against list_contacts.
                    // RAISE EXCEPTION fail-closed on missing/invalid/inactive
                    // list refs propagates to auto_enroll_new_member's
                    // per-sequence exception block, so a broken reference
                    // skips that sequence rather than enrolling everyone.
                    // event_performed / event_not_performed stay excluded —
                    // those behavioral fields are not yet supported in
                    // trigger conditions.
                    excludeFields={["event_performed", "event_not_performed"]}
                  />
                </div>

                {/* "Send to all new members" opt-in — only surfaces when
                    triggerType=member_signup AND conditions are empty.
                    Audit 2026-05-30: explicit operator confirmation that
                    every new member should be enrolled regardless of tier. */}
                {(triggerType === "member_signup" ||
                  triggerType === "reinstated_member") &&
                  !eventId &&
                  triggerConditions.conditions.length === 0 && (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={sendToAllNewMembers}
                          onChange={(e) => {
                            setSendToAllNewMembers(e.target.checked);
                            if (errors.triggerConditions) {
                              setErrors((prev) => ({
                                ...prev,
                                triggerConditions: "",
                              }));
                            }
                          }}
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-yellow-900">
                          {triggerType === "reinstated_member" ? (
                            <>
                              <strong>Send to all reinstated members</strong> —
                              check this to confirm you want every returning
                              member enrolled in this sequence regardless of
                              tier, geography, or any other attribute.
                            </>
                          ) : (
                            <>
                              <strong>Send to all new members</strong> — check
                              this to confirm you want every new active member
                              enrolled in this sequence regardless of tier,
                              geography, or any other attribute. Use only for
                              genuine global welcomes (e.g. Welcome Kit).
                            </>
                          )}
                        </span>
                      </label>
                    </div>
                  )}

                {errors.triggerConditions && (
                  <p className="text-sm text-red-600 mt-2">
                    {errors.triggerConditions}
                  </p>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* Section 3: Date-Based Trigger */}
        {showTriggerConfig &&
          triggerType !== "product_acquired" &&
          triggerType !== "reinstated_member" && (
            <Card>
              <div className="p-6 space-y-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Date-Based Trigger
                </h2>
                <p className="text-sm text-gray-500">
                  Optionally target contacts relative to a date field. For
                  example, "30 days before Member Renewal Date" will enroll
                  contacts whose renewal date is exactly 30 days away.
                </p>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date Field
                  </label>
                  <select
                    value={triggerDateField}
                    onChange={(e) => setTriggerDateField(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">None (no date trigger)</option>
                    {TRIGGER_DATE_FIELD_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {triggerDateField && (
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Offset (days)
                      </label>
                      <input
                        type="number"
                        min={0}
                        value={offsetDays}
                        onChange={(e) =>
                          setOffsetDays(
                            Math.max(0, parseInt(e.target.value) || 0),
                          )
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Direction
                      </label>
                      <select
                        value={offsetDirection}
                        onChange={(e) =>
                          setOffsetDirection(
                            e.target.value as "before" | "after",
                          )
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="before">Before the date</option>
                        <option value="after">After the date</option>
                      </select>
                    </div>
                  </div>
                )}

                {triggerDateField && offsetDays > 0 && (
                  <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                    This will match contacts whose{" "}
                    <strong>
                      {
                        TRIGGER_DATE_FIELD_OPTIONS.find(
                          (f) => f.value === triggerDateField,
                        )?.label
                      }
                    </strong>{" "}
                    is exactly{" "}
                    <strong>
                      {offsetDays} days {offsetDirection}
                    </strong>{" "}
                    today.
                  </p>
                )}
              </div>
            </Card>
          )}

        {/* Section 4: Event Link */}
        <Card>
          <div className="p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Event Link</h2>
            <p className="text-sm text-gray-500">
              Optionally link this sequence to an event. When combined with
              conditions, only contacts who registered for this event will be
              evaluated.
            </p>

            <div>
              <label
                htmlFor="sequence-event"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Event
              </label>
              <EventPicker
                value={eventId}
                inputId="sequence-event"
                testId="sequence-event-picker"
                noneLabel="No event linked — leave empty for a sequence that is not tied to an event."
                onChange={(id) => {
                  setEventId(id);
                  // Linking an event defaults an EMPTY category to "Events" (2026-09-30), so the
                  // Unsubscribe button only stops event emails. An existing choice is kept.
                  if (id && !subscriptionGroupId) {
                    const events = subscriptionGroups?.find(
                      (g) =>
                        g.sendgrid_group_id === 35127 || g.slug === "events",
                    );
                    if (events) {
                      setSubscriptionGroupId(events.id);
                    }
                  }
                }}
              />
            </div>

            {/* Event trigger mode — Pardot parity Tier 1 (2026-05-17) */}
            {eventId && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Trigger When Contact
                </label>
                <div className="flex flex-col gap-2">
                  <label className="inline-flex items-start gap-2 text-sm">
                    <input
                      type="radio"
                      checked={eventTriggerMode === "event_registration"}
                      onChange={() => setEventTriggerMode("event_registration")}
                      className="mt-0.5"
                    />
                    <span>
                      <strong>Registers</strong> — fires when the contact signs
                      up for the event
                    </span>
                  </label>
                  <label className="inline-flex items-start gap-2 text-sm">
                    <input
                      type="radio"
                      checked={eventTriggerMode === "event_attended"}
                      onChange={() => setEventTriggerMode("event_attended")}
                      className="mt-0.5"
                    />
                    <span>
                      <strong>Attends (checks in)</strong> — fires when the
                      contact is checked in at the event. Any contacts already
                      checked in when this sequence first activates will be
                      auto-enrolled on the next evaluation tick.
                    </span>
                  </label>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Section 5: Associated List */}
        <Card>
          <div className="p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Associated List
              {triggerTypeUsesListForEnrollment(triggerType) && !eventId && (
                <span className="text-red-500"> *</span>
              )}
            </h2>
            <p className="text-sm text-gray-500">
              {triggerTypeUsesListForEnrollment(triggerType) && !eventId
                ? "Required — this sequence enrolls contacts when they join the chosen list."
                : `Reference only. This sequence enrolls on ${TRIGGER_TYPE_ENROLLS_ON[triggerType]} — a list attached here does NOT limit who is enrolled.`}
            </p>

            {/*
              The warning that would have prevented the 2026-09-14 incident. A correct list
              ("Lifetime Members", 10 people) was attached to a member_signup sequence with no
              trigger conditions. It looked scoped; the enrolling code never reads list_id. 1,465
              people were enrolled and emailed. Shown at the exact moment the operator does the
              thing that misleads them: selecting a list on a trigger that ignores it.
            */}
            {!triggerTypeUsesListForEnrollment(triggerType) && listId && (
              <div
                role="alert"
                className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
              >
                <p className="font-medium">
                  This list will not limit who gets enrolled.
                </p>
                <p className="mt-1">
                  “
                  {TRIGGER_TYPE_OPTIONS.find((o) => o.value === triggerType)
                    ?.label ?? triggerType}
                  ” enrolls on {TRIGGER_TYPE_ENROLLS_ON[triggerType]}. The list
                  is stored for reference only and is not read when deciding who
                  to enroll.
                </p>
                <p className="mt-1">
                  To restrict the audience, use{" "}
                  <span className="font-medium">Trigger Conditions</span> above.
                  If you leave those empty, this sequence will enroll{" "}
                  <span className="font-medium">every</span> matching contact
                  regardless of the list.
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                List
              </label>
              <select
                value={listId}
                onChange={(e) => {
                  setListId(e.target.value);
                  if (errors.listId) {
                    setErrors((prev) => ({ ...prev, listId: "" }));
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">No list linked</option>
                {lists?.map((list: any) => (
                  <option key={list.id} value={list.id}>
                    {list.name} ({list.contact_count} contacts)
                  </option>
                ))}
              </select>
              {errors.listId && (
                <p className="text-sm text-red-600 mt-2">{errors.listId}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email subscription category
                {triggerType === "product_acquired" && !eventId && (
                  <span className="text-red-500"> *</span>
                )}
              </label>
              <select
                aria-label="Email subscription category"
                value={subscriptionGroupId}
                onChange={(e) => {
                  setSubscriptionGroupId(e.target.value);
                  if (errors.subscriptionGroupId) {
                    setErrors((prev) => ({
                      ...prev,
                      subscriptionGroupId: "",
                    }));
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">— None —</option>
                {subscriptionGroups?.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Associates emails from this sequence with a SendGrid unsubscribe
                group so the one-click unsubscribe and preference links resolve
                correctly.{" "}
                {triggerType === "product_acquired" && !eventId
                  ? "Required for product journeys."
                  : "Leave as “None” to send without a category."}
              </p>
              {errors.subscriptionGroupId && (
                <p role="alert" className="text-sm text-red-600 mt-1">
                  {errors.subscriptionGroupId}
                </p>
              )}
            </div>
          </div>
        </Card>

        {/* Section 6: Re-enrollment Policy (Pardot Tier 1 — 2026-05-17) */}
        <Card>
          <div className="p-6 space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">
              Re-enrollment Policy
            </h2>
            <label className="inline-flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                disabled={triggerType === "product_acquired" && !eventId}
                checked={allowReenrollment}
                onChange={(e) => setAllowReenrollment(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                <strong>Allow re-enrollment</strong> after a previous enrollment
                is completed or cancelled. Contacts who are currently active or
                paused in this sequence still cannot be re-enrolled.
              </span>
            </label>
          </div>
        </Card>

        {/* Error and Submit */}
        {mutation.isError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
            {mutation.error instanceof Error
              ? mutation.error.message
              : `Failed to ${isEditMode ? "update" : "create"} sequence`}
          </div>
        )}

        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            type="button"
            onClick={() =>
              navigate(isEditMode ? `/sequences/${id}` : "/sequences")
            }
          >
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={mutation.isPending}>
            {mutation.isPending
              ? isEditMode
                ? "Saving..."
                : "Creating..."
              : isEditMode
                ? "Save Changes"
                : "Create Sequence"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SequenceCreate;
